import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrainCircuit, Plus, CircleAlert as AlertCircle, RefreshCw } from 'lucide-react';
import { listOpportunities } from '../../lib/mavisApi';
import { useAuth } from '../../contexts/AuthContext';
import type { MavisOpportunityRow } from '../../types/mavis';
import MavisPipelineStats from '../../components/mavis/MavisPipelineStats';
import MavisForecastFilters from '../../components/mavis/MavisForecastFilters';
import MavisForecastTable from '../../components/mavis/MavisForecastTable';
import MavisCreateOpportunityModal from '../../components/mavis/MavisCreateOpportunityModal';

// Returns e.g. "Q3 2026" from a YYYY-MM-DD date string
function getQuarter(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

export default function Mavis() {
  const navigate = useNavigate();
  const { organisation } = useAuth();

  const [opportunities, setOpportunities] = useState<MavisOpportunityRow[]>([]);
  const [ownerMap, setOwnerMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [forecastLabelFilter, setForecastLabelFilter] = useState('');
  const [quarterFilter, setQuarterFilter] = useState('');

  const loadData = useCallback(async () => {
    if (!organisation) return;
    setLoading(true);
    setError(null);

    try {
      const { opportunities: data } = await listOpportunities(organisation.id);

      const rows = data as unknown as (MavisOpportunityRow & { owner_name?: string | null })[];
      setOpportunities(rows);

      // Owner display names come resolved from the API
      const map = new Map<string, string>();
      rows.forEach(r => {
        const name = r.owner_name?.trim();
        if (r.owner_user_id && name) map.set(r.owner_user_id, name);
      });
      setOwnerMap(map);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load opportunities. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [organisation]);

  useEffect(() => { loadData(); }, [loadData]);

  // Quarter options derived from loaded opportunities
  const quarterOptions = useMemo(() => {
    const qs = new Set<string>();
    opportunities.forEach(o => {
      if (o.target_close_date) qs.add(getQuarter(o.target_close_date));
    });
    // Sort chronologically: "Q1 2026" < "Q2 2026" etc.
    return Array.from(qs).sort((a, b) => {
      const [qa, ya] = a.split(' ');
      const [qb, yb] = b.split(' ');
      return Number(ya) - Number(yb) || qa.localeCompare(qb);
    });
  }, [opportunities]);

  // Client-side filtering
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return opportunities.filter(o => {
      if (q) {
        const titleMatch   = o.title.toLowerCase().includes(q);
        const accountMatch = (o.accounts?.company_name ?? '').toLowerCase().includes(q);
        if (!titleMatch && !accountMatch) return false;
      }
      if (stageFilter && o.stage !== stageFilter) return false;
      if (forecastLabelFilter && o.forecast_label !== forecastLabelFilter) return false;
      if (quarterFilter) {
        if (!o.target_close_date) return false;
        if (getQuarter(o.target_close_date) !== quarterFilter) return false;
      }
      return true;
    });
  }, [opportunities, search, stageFilter, forecastLabelFilter, quarterFilter]);

  const handleCreated = useCallback(async (newId: string) => {
    setShowCreateModal(false);
    // Refresh the table so the new row is visible, then navigate to its detail page
    await loadData();
    navigate(`/app/mavis/${newId}`);
  }, [loadData, navigate]);

  const hasActiveFilters = search !== '' || stageFilter !== '' || forecastLabelFilter !== '' || quarterFilter !== '';

  return (
    <div className="min-h-screen bg-app-canvas">
      <div className="px-8 py-8 space-y-6">

        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-0.5">
              <BrainCircuit className="w-7 h-7 text-forest-600" />
              <h1 className="text-2xl font-bold text-app-ink">MAVIS</h1>
            </div>
            <p className="text-sm text-app-muted">Operational opportunity dashboard</p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 text-app-muted hover:text-app-muted hover:bg-app-surface border border-transparent hover:border-app-line rounded-lg transition-all"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Opportunity
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 flex-1">{error}</p>
            <button
              onClick={loadData}
              className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline whitespace-nowrap"
            >
              Retry
            </button>
          </div>
        )}

        {/* Pipeline stats — always computed from ALL opportunities, not the filtered set */}
        <MavisPipelineStats opportunities={opportunities} />

        {/* Filters */}
        <MavisForecastFilters
          search={search}
          stage={stageFilter}
          forecastLabel={forecastLabelFilter}
          quarter={quarterFilter}
          quarterOptions={quarterOptions}
          onSearchChange={setSearch}
          onStageChange={setStageFilter}
          onForecastLabelChange={setForecastLabelFilter}
          onQuarterChange={setQuarterFilter}
        />

        {/* Active-filter indicator */}
        {hasActiveFilters && !loading && (
          <p className="text-xs text-app-muted">
            Showing {filtered.length} of {opportunities.length} opportunities
            {' '}
            <button
              onClick={() => { setSearch(''); setStageFilter(''); setForecastLabelFilter(''); setQuarterFilter(''); }}
              className="text-forest-600 hover:underline"
            >
              Clear filters
            </button>
          </p>
        )}

        {/* Forecast table */}
        <MavisForecastTable
          opportunities={filtered}
          ownerMap={ownerMap}
          loading={loading}
          onRowClick={id => navigate(`/app/mavis/${id}`)}
        />
      </div>

      <MavisCreateOpportunityModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
